import clsx from "clsx";
import { useIntl } from "react-intl";

import { CollapsibleCard } from "@/components/CollapsibleCard/CollapsibleCard";
import type { FindPackageByIdCountry } from "@/types/codegen";

type Props = {
  countries: FindPackageByIdCountry[];
};

export function VisaPermissionsCard({ countries }: Props) {
  const intl = useIntl();

  return (
    <CollapsibleCard
      title={intl.formatMessage({
        defaultMessage: "Visa permissions",
        description:
          "Section heading for visa entry requirements for the trip destination",
      })}
      subtitle={intl.formatMessage({
        defaultMessage: "Visa requirements and assistance details",
        description: "Subtitle below the visa requirements section heading",
      })}
      collapsible={false}
    >
      <ul className="space-y-2">
        {countries?.map(({ id, name, visaType }) => {
          const getVisaTranslation = () => {
            switch (visaType) {
              case "E_VISA":
                return intl.formatMessage({
                  defaultMessage: "e-Visa",
                  description: "Entry requirement option: e-Visa required",
                });
              case "VISA_ON_ARRIVAL":
                return intl.formatMessage({
                  defaultMessage: "Visa on arrival",
                  description: "Entry requirement option: visa on arrival",
                });
              case "VISA_FREE":
                return intl.formatMessage({
                  defaultMessage: "Visa free",
                  description: "Entry requirement option: visa-free entry",
                });
              case "VISA_REQUIRED":
                return intl.formatMessage({
                  defaultMessage: "Visa required",
                  description:
                    "Entry requirement option: visa required (must be obtained in advance)",
                });
              default:
                return visaType; // Fallback to raw value if no translation is found
            }
          };

          return (
            <li
              key={id}
              className={clsx(
                "flex items-center justify-between rounded-xl p-3 text-sm font-medium text-text-primary",
                visaType === "VISA_FREE"
                  ? "bg-surface-accent-softer"
                  : visaType === "VISA_REQUIRED"
                    ? "bg-semantic-error-pressed-soft"
                    : "bg-semantic-warning-soft",
              )}
            >
              <span>{name}</span>
              <span>{getVisaTranslation()}</span>
            </li>
          );
        })}
      </ul>
    </CollapsibleCard>
  );
}
